/**
 * Global in-flight-request counter — the single source of truth for the app-wide top progress bar
 * (see components/TopProgressBar). Every real network call through `apiFetch` (client.ts) brackets
 * itself with begin()/end(), so the bar reflects ALL requests automatically with zero per-call code.
 * This is the load-bearing half of the app's loading-feedback convention (docs/design/loading-feedback.md):
 * optimistic UI for the instant feel + this global bar for the "something's happening" signal.
 *
 * A tiny hand-rolled store (not a context) so non-React code — the api client — can drive it.
 */
let count = 0;
const listeners = new Set<() => void>();

function emit(): void {
  for (const l of listeners) l();
}

/** Mark a request as started (call in a try/finally with endRequest). */
export function beginRequest(): void {
  count += 1;
  emit();
}

/** Mark a request as finished (settled — success OR error). */
export function endRequest(): void {
  count = Math.max(0, count - 1);
  emit();
}

/** How many requests are currently in flight. */
export function getPendingCount(): number {
  return count;
}

/** Subscribe to changes; returns an unsubscribe fn. Shaped for React's useSyncExternalStore. */
export function subscribePending(onChange: () => void): () => void {
  listeners.add(onChange);
  return () => listeners.delete(onChange);
}
